import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Bug, Upload, X, Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitBugReport } from "@/lib/api";

const MAX_MB = 10;
const ALLOWED = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif"];

export default function ReportBug({ trigger }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  const reset = () => {
    setName(""); setEmail(""); setDescription(""); setFile(null); setPreview(null); setDone(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleFile = (f) => {
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      toast.error("Screenshot must be PNG, JPG, WEBP or GIF");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Screenshot too large (max ${MAX_MB} MB)`);
      return;
    }
    setFile(f);
    const url = URL.createObjectURL(f);
    setPreview(url);
  };

  const removeFile = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null); setPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!name.trim() || !email.trim() || !description.trim()) {
      toast.error("Name, email and description are required");
      return;
    }
    if (description.trim().length < 10) {
      toast.error("Please describe the bug in a bit more detail");
      return;
    }
    setSubmitting(true);
    try {
      await submitBugReport({ name, email, description, screenshot: file });
      setDone(true);
      toast.success("Bug report sent — thanks for helping us improve!");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed to send report";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setTimeout(reset, 250);
      }}
    >
      <DialogTrigger asChild>
        {trigger || (
          <button
            data-testid="open-report-bug-btn"
            className="inline-flex items-center gap-2 border border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white hover:border-yellow-500 hover:text-yellow-500 font-semibold text-sm px-3 h-9 transition-all"
          >
            <Bug className="w-4 h-4" strokeWidth={2.5} />
            Report a bug
          </button>
        )}
      </DialogTrigger>
      <DialogContent
        data-testid="report-bug-dialog"
        className="max-w-lg bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-none p-0 gap-0"
      >
        <DialogHeader className="p-6 border-b border-zinc-200 dark:border-zinc-800 text-left">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-500 text-black grid place-items-center">
              <Bug className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div>
              <DialogTitle className="font-display font-bold text-xl text-zinc-900 dark:text-white">
                Report a bug
              </DialogTitle>
              <DialogDescription className="text-sm text-zinc-600 dark:text-zinc-400">
                Got stuck? Report a bug — help us build it better.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {done ? (
          <div
            data-testid="report-bug-success"
            className="p-10 flex flex-col items-center gap-4 text-center"
          >
            <div className="w-14 h-14 rounded-full bg-green-500/15 grid place-items-center">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
            <h4 className="font-display font-bold text-2xl text-zinc-900 dark:text-white">
              Report sent ✓
            </h4>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 max-w-xs">
              We&apos;ll reply to <span className="font-mono">{email}</span> if we need more info.
            </p>
            <button
              data-testid="report-bug-close-btn"
              onClick={() => setOpen(false)}
              className="mt-2 bg-yellow-500 hover:bg-yellow-400 text-black font-bold px-6 h-11 transition-all"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <Field label="Your name">
              <input
                data-testid="report-bug-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                required
                className="w-full bg-transparent border border-zinc-300 dark:border-zinc-800 h-11 px-3 text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-600 focus:outline-none focus:border-yellow-500 transition-colors"
              />
            </Field>

            <Field label="Your email">
              <input
                data-testid="report-bug-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@company.com"
                required
                className="w-full bg-transparent border border-zinc-300 dark:border-zinc-800 h-11 px-3 text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-600 focus:outline-none focus:border-yellow-500 transition-colors"
              />
            </Field>

            <Field label="What went wrong?" hint={`${description.length}/5000`}>
              <textarea
                data-testid="report-bug-description"
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, 5000))}
                placeholder="Steps to reproduce, what you expected, what actually happened…"
                rows={5}
                required
                minLength={10}
                className="w-full bg-transparent border border-zinc-300 dark:border-zinc-800 p-3 text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-600 focus:outline-none focus:border-yellow-500 transition-colors resize-none text-sm leading-relaxed"
              />
            </Field>

            <Field label="Screenshot (optional)" hint={`PNG · JPG · WEBP · max ${MAX_MB} MB`}>
              {preview ? (
                <div className="relative border border-zinc-300 dark:border-zinc-800 p-2 flex items-center gap-3">
                  <img
                    src={preview}
                    alt="Screenshot preview"
                    className="w-16 h-16 object-cover border border-zinc-200 dark:border-zinc-800"
                  />
                  <div className="flex-1 text-sm">
                    <div
                      data-testid="report-bug-file-name"
                      className="font-mono text-zinc-900 dark:text-white truncate"
                    >
                      {file?.name}
                    </div>
                    <div className="text-xs text-zinc-500">
                      {(file?.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeFile}
                    data-testid="report-bug-remove-file-btn"
                    className="h-9 w-9 grid place-items-center border border-zinc-300 dark:border-zinc-800 hover:border-red-500 hover:text-red-500 text-zinc-500 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label
                  data-testid="report-bug-upload-label"
                  className="border-2 border-dashed border-zinc-300 dark:border-zinc-700 hover:border-yellow-500 flex items-center gap-3 p-4 cursor-pointer transition-colors group"
                >
                  <div className="w-10 h-10 grid place-items-center border border-zinc-300 dark:border-zinc-700 group-hover:border-yellow-500 group-hover:bg-yellow-500 group-hover:text-black text-zinc-600 dark:text-zinc-400 transition-colors">
                    <Upload className="w-4 h-4" />
                  </div>
                  <div className="flex-1 text-sm">
                    <div className="font-semibold text-zinc-900 dark:text-white">
                      Click to upload screenshot
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      Or drag and drop
                    </div>
                  </div>
                  <input
                    ref={fileRef}
                    data-testid="report-bug-file-input"
                    type="file"
                    accept={ALLOWED.join(",")}
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    className="sr-only"
                  />
                </label>
              )}
            </Field>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-white px-4 h-11 transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="report-bug-submit-btn"
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 disabled:opacity-60 disabled:cursor-not-allowed text-black font-bold h-11 px-6 transition-all hover:-translate-y-0.5"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" strokeWidth={2.5} />
                    Send report
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">
          {label}
        </label>
        {hint && (
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 dark:text-zinc-600">
            {hint}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}
